import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input, ViewChild, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller/lib/ngx-virtual-scroller.component';
import { BehaviorSubject, combineLatest, debounceTime, filter, map, startWith, tap } from 'rxjs';

import { debounceAsap } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  GroupsFeatureDirective,
  HeaderDefaultComponent,
  IconButtonComponent,
  InputComponent,
  OverlayPosition,
  SearchFeatureDirective,
  Select2Component,
  Select2TriggerButtonComponent,
  Select2TriggerDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { ViewFilterDef } from '@core/views-types';

import type { FilterSelectOptions } from '../../../services/filters-data.service';
import { FiltersDataService } from '../../../services/filters-data.service';

@Component({
  selector: 'rt-views-filter-select',
  imports: [
    ReactiveFormsModule,
    Select2Component,
    GroupsFeatureDirective,
    SearchFeatureDirective,
    HeaderDefaultComponent,
    TranslatePipe,
    IconButtonComponent,
    InputComponent,
    Select2TriggerDirective,
    Select2TriggerButtonComponent,
  ],
  template: `
    <rt-select2
      #filterSelect
      [formControl]="filterSelectControl"
      [groupedItems]="filterSelectOptions?.options ?? []"
      [hiddenItems]="hiddenItems"
      [autoClose]="false"
      [itemsLoading]="filterSelectOptions ? !filterSelectOptions.loaded : true"
      [overlayWidth]="250"
      [overlayPosition]="overlayPosition"
      [disableLocalSearch]="true"
      [searchControl]="searchControl"
      [vsCompareItems]="vsCompareItems"
      (close)="onClose()"
      (vsEnd)="onScrollEnd($event)"
      withGroups
      withSearch
    >
      <rt-select-header>
        @if (filterSelectOptions?.groupDef) {
          <rt-icon-button iconName="arrow-back" variant="plain" prefix (click)="onBackClick()" />
        }
        @if (filterSelectOptions?.groupDef; as groupDef) {
          {{ groupDef.name }}
        }
        @if (!filterSelectOptions?.groupDef) {
          {{ 'app.views.filters.add_filter' | translate }}
        }
      </rt-select-header>
      <ng-template
        rtSelectTrigger
        #triggerDir="triggerDir"
        let-selectCtrl
        let-inOverlay="inOverlay"
      >
        @if (trigger === 'input') {
          <rt-input
            [readOnly]="true"
            [placeholder]="'app.views.filters.add_filter' | translate"
            [hasDropdownIndicator]="true"
            [class.dropdownOpened]="selectCtrl.isOpen()"
            [value]="filterDef?.name ?? null"
            (click)="selectCtrl.toggle(true)"
            variant="rounded"
          />
        } @else if (trigger === 'button') {
          <rt-select-trigger-button
            [label]="'app.views.filters.add_filter' | translate"
            [icon]="'add'"
            [dropdownIconVisible]="false"
            buttonVariant="secondary"
          />
        }
      </ng-template>
    </rt-select2>
  `,
})
export class FilterSelectComponent {
  public readonly overlayPosition = [
    OverlayPosition.BottomLeft,
    OverlayPosition.TopLeft,
    OverlayPosition.BottomRight,
    OverlayPosition.TopRight,
  ];

  @Input() public filterDef: ViewFilterDef | null = null;
  @Input() public trigger: 'button' | 'input' = 'input';

  public filterChange = output<string>();

  public filterSelectControl = new FormControl<string | null>(null);
  public filterSelectOptions: FilterSelectOptions | null = null;
  public hiddenItems: Select2Item[] = [];
  public searchControl = new FormControl<string | null>(null);

  @ViewChild('filterSelect') private filterSelect: Select2Component<Select2Item>;
  private filtersDataService = inject(FiltersDataService);
  private cdRef = inject(ChangeDetectorRef);
  private untilDestroyed = injectUntilDestroyed();
  private scrollEndIndex$ = new BehaviorSubject<number>(0);

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.filterDef && this.filterDef) {
      this.filterSelectControl.setValue(this.filterDef.id);
      this.hiddenItems = [this.filtersDataService.filterToSelectOption(this.filterDef)];
    }
  }

  public ngOnInit() {
    combineLatest([
      this.filterSelectControl.valueChanges.pipe(startWith(this.filterSelectControl.value)),
      this.filtersDataService.dataSourceFilterDefMap$,
    ])
      .pipe(debounceAsap(), this.untilDestroyed)
      .subscribe(([filterId, dataSourceFilterDefMap]) => {
        const isEmpty = filterId === null;
        if (isEmpty) return;
        const isGroup = dataSourceFilterDefMap[filterId]?.type === 'filter_group';
        const isEqual = filterId === this.filterDef?.id;
        if (isGroup || isEqual) return;

        this.filterSelect.selectController.isOpen.set(false);
        this.filterChange.emit(filterId);
        this.scrollEndIndex$.next(0);
      });

    const filterOrGroupId$ = new BehaviorSubject<string | null>(this.filterSelectControl.value);
    this.filterSelectControl.valueChanges.pipe(this.untilDestroyed).subscribe(filterOrGroupId$);
    this.filtersDataService
      .getFilterSelectOptions$(
        filterOrGroupId$,
        this.getSearchQuery$(),
        this.scrollEndIndex$.pipe(filter(index => index > 0)),
      )
      .pipe(this.untilDestroyed)
      .subscribe(selectOptions => {
        this.filterSelectOptions = selectOptions;
        this.cdRef.detectChanges();
      });

    this.filterSelectControl.valueChanges
      .pipe(this.untilDestroyed)
      .subscribe(() => this.cdRef.detectChanges());
  }

  public vsCompareItems = (item1: Select2Item | undefined, item2: Select2Item | undefined) =>
    item1?.label === item2?.label;

  public onBackClick() {
    this.filterSelectControl.setValue(null);
  }

  public onClose() {
    const result = this.filterDef?.id ?? null;
    this.filterSelectControl.setValue(result);
  }

  public onScrollEnd(event: IPageInfo) {
    const endIndex = event.endIndexWithBuffer;
    if (endIndex !== -1 && endIndex < this.scrollEndIndex$.value) return;

    this.scrollEndIndex$.next(endIndex);
  }

  private getSearchQuery$() {
    return this.searchControl.valueChanges.pipe(
      debounceTime(100),
      startWith(this.searchControl.value),
      map(value => (value ?? '').trim().toLowerCase()),
      tap(() => {
        this.filterSelect.scrollTop();
        this.scrollEndIndex$.next(0);
      }),
    );
  }
}
